import React, { useState } from 'react';
import { RefreshCw, ChevronDown, ChevronUp, Palette, Search, Settings, Eye, Download, Brain, FileText, Bell } from 'lucide-react';
import Modal from './Modal';
import SlackSettings from './SlackSettings';

const DEFAULT_SETTINGS = {
    darkMode: true,
    hideThreshold: 30,
    greyThreshold: 50,
    highConfidenceThreshold: 80,
    defaultExportFormat: 'json',
    jsonIndentation: 2,
    // Diff Highlighting Settings
    highlightRenames: true,
    highlightMoves: true,
    highlightCascadingImpact: true,
    highlightTypeChanges: true,
    highlightNullDifferences: true,
    showTypeAnnotations: true,
    // EDI Viewer Settings
    showElementLabels: true,
    showLoopHierarchy: false,
    groupLoopSegments: true,
    tabularLineItems: true,
    showEmptyElements: false,
    // Advanced Semantic Matching Settings
    contextWeight: 0.3,
    arraySemanticMatching: true,
    unionTypeDetection: true,
    typeEvolutionWarnings: true,
};

// Collapsible Section Component
const SettingsSection = ({ title, icon: Icon, children, defaultOpen = false }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    return (
        <div className="border border-[#3c3c3c] rounded-lg overflow-hidden">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between p-4 bg-[#252526] hover:bg-[#3c3c3c] transition-colors"
            >
                <div className="flex items-center gap-3">
                    <Icon size={18} className="text-[#808080]" />
                    <span className="font-medium text-white">{title}</span>
                </div>
                {isOpen ? <ChevronUp size={18} className="text-slate-400" /> : <ChevronDown size={18} className="text-slate-400" />}
            </button>
            {isOpen && (
                <div className="p-4 bg-[#1e1e1e]">
                    {children}
                </div>
            )}
        </div>
    );
};

const ToggleSetting = ({ label, description, value, onChange, color = "blue" }) => (
    <div className="flex items-center justify-between py-2">
        <div>
            <span className="text-sm font-medium text-[#cccccc] block">{label}</span>
            {description && <p className="text-xs text-[#808080]">{description}</p>}
        </div>
        <button
            onClick={() => onChange(!value)}
            className={`relative w-10 h-5 rounded-full transition-colors ${value ? 'bg-blue-500' : 'bg-[#4c4c4c]'}`}
        >
            <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform shadow ${value ? 'left-5' : 'left-0.5'
                }`} />
        </button>
    </div>
);

const SliderSetting = ({ label, value, onChange, min, max, step, unit = "%", color = "blue", description }) => (
    <div className="py-2">
        <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-[#cccccc]">{label}</label>
            <span className={`text-sm font-bold text-${color}-400 bg-${color}-900/30 px-2 py-0.5 rounded`}>
                {value}{unit}
            </span>
        </div>
        <input
            type="range"
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={(e) => onChange(parseFloat(e.target.value))}
            className="w-full h-2 bg-[#3c3c3c] rounded-lg appearance-none cursor-pointer accent-blue-500"
        />
        {description && <p className="text-xs text-[#808080] mt-1">{description}</p>}
    </div>
);

const SettingsModal = ({ isOpen, onClose, settings, onSettingsChange }) => {
    const handleChange = (key, value) => {
        onSettingsChange({ ...settings, [key]: value });
    };

    const handleResetAll = () => {
        onSettingsChange({ ...DEFAULT_SETTINGS, darkMode: settings.darkMode });
    };

    const handleResetThresholds = () => {
        onSettingsChange({
            ...settings,
            hideThreshold: DEFAULT_SETTINGS.hideThreshold,
            greyThreshold: DEFAULT_SETTINGS.greyThreshold,
            highConfidenceThreshold: DEFAULT_SETTINGS.highConfidenceThreshold,
            contextWeight: DEFAULT_SETTINGS.contextWeight,
        });
    };

    const handleThresholdChange = (key, value) => {
        let newValue = parseFloat(value);
        const newSettings = { ...settings, [key]: newValue };

        if (key === 'hideThreshold' && newValue >= newSettings.greyThreshold) {
            newSettings.greyThreshold = newValue + 5;
        }
        if (key === 'greyThreshold') {
            if (newValue <= newSettings.hideThreshold) {
                newSettings.hideThreshold = newValue - 5;
            }
            if (newValue >= newSettings.highConfidenceThreshold) {
                newSettings.highConfidenceThreshold = newValue + 5;
            }
        }
        if (key === 'highConfidenceThreshold' && newValue <= newSettings.greyThreshold) {
            newSettings.greyThreshold = newValue - 5;
        }

        newSettings.hideThreshold = Math.max(0, Math.min(40, newSettings.hideThreshold));
        newSettings.greyThreshold = Math.max(newSettings.hideThreshold + 5, Math.min(75, newSettings.greyThreshold));
        newSettings.highConfidenceThreshold = Math.max(newSettings.greyThreshold + 5, Math.min(95, newSettings.highConfidenceThreshold));

        onSettingsChange(newSettings);
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Settings"
            maxWidth="max-w-lg"
        >
            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">


                {/* Semantic Matching Section */}
                <SettingsSection title="Semantic Matching" icon={Search} defaultOpen={false}>
                    <div className="space-y-1">
                        <SliderSetting
                            label="Hide Below"
                            value={settings.hideThreshold}
                            onChange={(v) => handleThresholdChange('hideThreshold', v)}
                            min={0}
                            max={40}
                            step={5}
                            color="red"
                            description="Rename suggestions below this confidence won't appear"
                        />
                        <SliderSetting
                            label="Uncertain Below"
                            value={settings.greyThreshold}
                            onChange={(v) => handleThresholdChange('greyThreshold', v)}
                            min={25}
                            max={75}
                            step={5}
                            color="yellow"
                            description="Matches below this will appear greyed out with 'uncertain' label"
                        />
                        <SliderSetting
                            label="High Confidence"
                            value={settings.highConfidenceThreshold}
                            onChange={(v) => handleThresholdChange('highConfidenceThreshold', v)}
                            min={55}
                            max={95}
                            step={5}
                            color="green"
                            description="Matches above this are high confidence (suggested for auto-apply)"
                        />

                        {/* Confidence Zones Preview */}
                        <div className="mt-4 pt-4 border-t border-[#3c3c3c]">
                            <p className="text-xs font-medium text-[#808080] mb-2">Confidence Zones:</p>
                            <div className="flex items-center gap-2 text-xs flex-wrap">
                                <span className="px-2 py-1 bg-red-900/30 text-red-400 rounded">
                                    0-{settings.hideThreshold}% Hidden
                                </span>
                                <span className="px-2 py-1 bg-yellow-900/30 text-yellow-400 rounded">
                                    {settings.hideThreshold}-{settings.greyThreshold}% Uncertain
                                </span>
                                <span className="px-2 py-1 bg-orange-900/30 text-orange-400 rounded">
                                    {settings.greyThreshold}-{settings.highConfidenceThreshold}% Confident
                                </span>
                                <span className="px-2 py-1 bg-green-900/30 text-green-400 rounded">
                                    {settings.highConfidenceThreshold}%+ High Conf
                                </span>
                            </div>
                        </div>

                        <div className="mt-4 pt-4 border-t border-[#3c3c3c]">
                            <button
                                onClick={handleResetThresholds}
                                className="text-blue-400 hover:underline flex items-center gap-1"
                            >
                                <RefreshCw size={12} />
                                Reset to defaults
                            </button>
                        </div>
                    </div>
                </SettingsSection>

                {/* Advanced Matching Section */}
                <SettingsSection title="Advanced Matching" icon={Brain}>
                    <div className="space-y-1">
                        <SliderSetting
                            label="Context Weight"
                            value={Math.round(settings.contextWeight * 100)}
                            onChange={(v) => handleChange('contextWeight', v / 100)}
                            min={0}
                            max={100}
                            step={10}
                            color="blue"
                            description="Higher values prioritize parent path matching (user.id vs order_item.id)"
                        />
                        <div className="border-t border-[#3c3c3c] pt-2 mt-2">
                            <ToggleSetting
                                label="Array Semantic Matching"
                                description="Match array elements by ID fields instead of position"
                                value={settings.arraySemanticMatching}
                                onChange={(v) => handleChange('arraySemanticMatching', v)}
                            />
                            <ToggleSetting
                                label="Union Type Detection"
                                description="Detect oneOf/anyOf patterns and variant changes"
                                value={settings.unionTypeDetection}
                                onChange={(v) => handleChange('unionTypeDetection', v)}
                            />
                            <ToggleSetting
                                label="Type Evolution Warnings"
                                description="Alert on complex type changes (string→object, array→object)"
                                value={settings.typeEvolutionWarnings}
                                onChange={(v) => handleChange('typeEvolutionWarnings', v)}
                            />
                        </div>
                    </div>
                </SettingsSection>

                {/* Diff Highlighting Section */}
                <SettingsSection title="Diff Highlighting" icon={Eye}>
                    <div className="space-y-1">
                        <ToggleSetting
                            label="Highlight Renames"
                            description="Orange highlight for AI-detected field renames"
                            value={settings.highlightRenames}
                            onChange={(v) => handleChange('highlightRenames', v)}
                            color="orange"
                        />
                        <ToggleSetting
                            label="Highlight Moves"
                            description="Purple highlight for fields moved in hierarchy"
                            value={settings.highlightMoves}
                            onChange={(v) => handleChange('highlightMoves', v)}
                            color="purple"
                        />
                        <ToggleSetting
                            label="Cascading Impact"
                            description="Mark unchanged fields affected by parent renames"
                            value={settings.highlightCascadingImpact}
                            onChange={(v) => handleChange('highlightCascadingImpact', v)}
                            color="blue"
                        />
                        <ToggleSetting
                            label="Type Changes"
                            description="Warn when type changes (string→number, etc.)"
                            value={settings.highlightTypeChanges}
                            onChange={(v) => handleChange('highlightTypeChanges', v)}
                            color="yellow"
                        />
                        <ToggleSetting
                            label="Null/Missing Differences"
                            description="Distinguish between null, empty, and missing fields"
                            value={settings.highlightNullDifferences}
                            onChange={(v) => handleChange('highlightNullDifferences', v)}
                        />
                        <ToggleSetting
                            label="Show Type Annotations"
                            description="Display the inferred type (string, number, object…) next to each field"
                            value={settings.showTypeAnnotations}
                            onChange={(v) => handleChange('showTypeAnnotations', v)}
                        />

                        <div className="mt-4 pt-4 border-t border-[#3c3c3c]">
                            <p className="text-xs font-medium text-[#808080] mb-2">Color Legend:</p>
                            <div className="flex items-center gap-2 flex-wrap text-xs">
                                <span className="px-2 py-0.5 bg-red-900/30 text-red-400 rounded">Removed</span>
                                <span className="px-2 py-0.5 bg-green-900/20 text-green-400 rounded">Added</span>
                                <span className="px-2 py-0.5 bg-orange-900/30 text-orange-400 rounded">Renamed</span>
                                <span className="px-2 py-0.5 bg-purple-900/30 text-purple-300 rounded">Moved</span>
                                <span className="px-2 py-0.5 bg-yellow-900/30 text-yellow-400 rounded">Type</span>
                                <span className="px-2 py-0.5 bg-blue-900/30 text-blue-300 rounded">Impact</span>
                            </div>
                        </div>
                    </div>
                </SettingsSection>

                {/* EDI Viewer Section */}
                <SettingsSection title="EDI Viewer" icon={FileText}>
                    <div className="space-y-1">
                        <ToggleSetting
                            label="Element Labels"
                            description="Show field names above values (Quantity, Unit Price)"
                            value={settings.showElementLabels}
                            onChange={(v) => handleChange('showElementLabels', v)}
                        />
                        <ToggleSetting
                            label="Loop Hierarchy"
                            description="Show loop context (Loop: N1, Parent: ST)"
                            value={settings.showLoopHierarchy}
                            onChange={(v) => handleChange('showLoopHierarchy', v)}
                        />
                        <ToggleSetting
                            label="Group Loop Segments"
                            description="Collapse N1-N4 into collapsible party blocks"
                            value={settings.groupLoopSegments}
                            onChange={(v) => handleChange('groupLoopSegments', v)}
                        />
                        <ToggleSetting
                            label="Tabular Line Items"
                            description="Show PO1/IT1 segments as table grid"
                            value={settings.tabularLineItems}
                            onChange={(v) => handleChange('tabularLineItems', v)}
                        />
                        <ToggleSetting
                            label="Show Empty Elements"
                            description="Display fields with no value"
                            value={settings.showEmptyElements}
                            onChange={(v) => handleChange('showEmptyElements', v)}
                        />
                    </div>
                </SettingsSection>

                {/* Export Section */}
                <SettingsSection title="Export" icon={Download}>
                    <div className="space-y-4">
                        <div>
                            <label className="text-[#cccccc] block mb-2">
                                Default Contract Export Format
                            </label>
                            <div className="grid grid-cols-3 gap-2">
                                {[
                                    { id: 'json', label: 'JSON' },
                                    { id: 'yaml', label: 'YAML' },
                                    { id: 'csv', label: 'CSV' },
                                ].map((format) => (
                                    <button
                                        key={format.id}
                                        onClick={() => handleChange('defaultExportFormat', format.id)}
                                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${settings.defaultExportFormat === format.id
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-[#3c3c3c] text-[#cccccc] hover:bg-[#4c4c4c]'
                                            }`}
                                    >
                                        {format.label}
                                    </button>
                                ))}
                            </div>
                            <p className="text-[#808080] mt-2">
                                Format used when exporting contracts and comparison reports
                            </p>
                        </div>

                        <div className="pt-3 border-t border-[#3c3c3c]">
                            <label className="text-sm font-medium text-[#cccccc] block mb-2">
                                Schema Indentation
                            </label>
                            <div className="flex gap-2">
                                {[2, 4].map((spaces) => (
                                    <button
                                        key={spaces}
                                        onClick={() => handleChange('jsonIndentation', spaces)}
                                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${settings.jsonIndentation === spaces
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-[#3c3c3c] text-[#cccccc] hover:bg-[#4c4c4c]'
                                            }`}
                                    >
                                        {spaces} spaces
                                    </button>
                                ))}
                            </div>
                            <p className="text-xs text-[#808080] mt-2">
                                Indentation level for exported JSON/YAML schemas
                            </p>
                        </div>
                    </div>
                </SettingsSection>

                {/* Slack Settings Section */}
                <SettingsSection title="Slack Notifications" icon={Bell}>
                    <SlackSettings />
                </SettingsSection>

                {/* Reset All Button */}
                <button
                    onClick={handleResetAll}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-[#4c4c4c] text-[#cccccc] rounded-lg hover:bg-[#3c3c3c] transition-colors"
                >
                    <RefreshCw size={16} />
                    Reset All Settings
                </button>

                <p className="text-center text-[11px] text-[#555]">
                    Press <span className="font-mono bg-[#3c3c3c] text-[#808080] px-1 py-0.5 rounded">?</span> in the Comparator to view keyboard shortcuts
                </p>
            </div>
        </Modal>
    );
};

export { DEFAULT_SETTINGS };
export default SettingsModal;
